import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Search, MapPin, Eye, Siren, AlertTriangle, CheckCircle2, XCircle, Clock, Timer } from 'lucide-react';
import api, { formatApiError } from '../lib/api';
import { IncidentDialog } from '../components/dialogs/IncidentDialog';
import { toast } from 'sonner';
import { useLiveEvents } from '../lib/events';
import { computeSla, fmtSlaShort, SLA_TONE_META } from '../lib/sla';

const STATUS_BADGE = {
  open: 'bg-red-100 text-red-700 border-red-200',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
  pending_approval: 'bg-violet-100 text-violet-700 border-violet-200',
  closed: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-slate-200 text-slate-600 border-slate-300',
};

const STATUS_LABEL = {
  open: 'Open', in_progress: 'In Progress',
  pending_approval: 'Pending Approval',
  closed: 'Closed', cancelled: 'Cancelled',
};

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export const OccurrenceBook = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dialogDefaultTab, setDialogDefaultTab] = useState('details');
  // Live tick for SLA cells in the list — recompute every 30s
  const [, slaTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => slaTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusTab === 'all' ? {} : { status_filter: statusTab };
      const [iRes, cRes, krRes, sRes] = await Promise.all([
        api.get('/incidents', { params }),
        api.get('/customers'),
        api.get('/contractors'),
        api.get('/services'),
      ]);
      setIncidents(iRes.data || []);
      setCustomers(cRes.data || []);
      setContractors(krRes.data || []);
      setServices(sRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [statusTab]);

  useEffect(() => { load(); }, [load]);

  // Real-time refresh — re-load list whenever any incident event fires.
  useLiveEvents(
    ['incident.created', 'incident.updated', 'incident.closed',
      'incident.cancelled', 'incident.reopened',
      'incident.close_requested', 'incident.close_rejected',
      'incident.assignment_added', 'incident.assignment_ended',
      'incident.note_added', 'incident.note_updated', 'incident.note_deleted',
      'incident.attachment_added', 'incident.attachment_updated'],
    () => { load(); },
  );

  const customerMap = useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);
  const serviceMap = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return incidents;
    return incidents.filter((i) =>
      (i.incident_no || '').toLowerCase().includes(q) ||
      (i.description || '').toLowerCase().includes(q) ||
      (i.location_address || '').toLowerCase().includes(q) ||
      (customerMap[i.customer_id]?.company_name || '').toLowerCase().includes(q)
    );
  }, [incidents, search, customerMap]);

  const counts = useMemo(() => ({
    open: incidents.filter((i) => i.status === 'open').length,
    in_progress: incidents.filter((i) => i.status === 'in_progress').length,
    pending_approval: incidents.filter((i) => i.status === 'pending_approval').length,
    closed: incidents.filter((i) => i.status === 'closed').length,
    cancelled: incidents.filter((i) => i.status === 'cancelled').length,
  }), [incidents]);

  const openCreate = () => { setEditing(null); setDialogDefaultTab('details'); setDialogOpen(true); };
  const openView = useCallback(async (inc, opts = {}) => {
    try {
      const r = await api.get(`/incidents/${inc.id}`);
      setEditing(r.data);
      setDialogDefaultTab(opts.defaultTab || 'details');
      setDialogOpen(true);
    } catch (e) {
      toast.error('Failed to load incident');
    }
  }, []);

  // Deep-link support: location.state.openIncidentId (from MapView) or ?incident=<id> query param
  useEffect(() => {
    const stateId = location.state?.openIncidentId;
    const stateTab = location.state?.defaultTab;
    const params = new URLSearchParams(location.search);
    const queryId = params.get('incident');
    const queryTab = params.get('tab');
    const targetId = stateId || queryId;
    if (!targetId) return;
    openView({ id: targetId }, { defaultTab: stateTab || queryTab || 'details' });
    // Clear the param/state so a back-navigation doesn't re-open
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, location.state, navigate, openView]);

  const handleSaved = (updated) => {
    if (updated && updated.id) {
      setEditing(updated);
      setIncidents((arr) => {
        const idx = arr.findIndex((i) => i.id === updated.id);
        if (idx >= 0) {
          const out = [...arr];
          out[idx] = updated;
          return out;
        }
        return [updated, ...arr];
      });
    } else {
      load();
    }
  };

  return (
    <Layout title="Occurrence Book" subtitle="Live Incident Register">
      <div className="space-y-6 animate-fade-in" data-testid="occurrence-book-page">

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Open" value={counts.open} icon={AlertTriangle} accent="#dc2626" pulse={counts.open > 0} testid="kpi-open" />
          <StatCard label="In Progress" value={counts.in_progress} icon={Clock} accent="#d97706" testid="kpi-in-progress" />
          <StatCard label="Pending Approval" value={counts.pending_approval} icon={Clock} accent="#7c3aed" pulse={counts.pending_approval > 0} testid="kpi-pending-approval" />
          <StatCard label="Closed" value={counts.closed} icon={CheckCircle2} accent="#16a34a" testid="kpi-closed" />
          <StatCard label="Cancelled" value={counts.cancelled} icon={XCircle} accent="#64748b" testid="kpi-cancelled" />
        </div>

        <Card className="bg-white border-slate-200" data-testid="incidents-table-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <CardTitle className="font-heading text-lg text-[#0D5B69]">Incident Register</CardTitle>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text" placeholder="Search incidents..."
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 w-56" data-testid="incident-search"
                  />
                </div>
                <Button
                  onClick={openCreate}
                  className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold"
                  data-testid="new-incident-btn"
                >
                  <Plus className="w-4 h-4" /> New Incident
                </Button>
              </div>
            </div>
            <Tabs value={statusTab} onValueChange={setStatusTab} className="mt-3">
              <TabsList>
                <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                <TabsTrigger value="open" data-testid="filter-open">Open</TabsTrigger>
                <TabsTrigger value="in_progress" data-testid="filter-in-progress">In Progress</TabsTrigger>
                <TabsTrigger value="pending_approval" data-testid="filter-pending-approval">
                  Pending Approval
                  {counts.pending_approval > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-violet-600 text-white text-[10px] rounded-full font-bold">
                      {counts.pending_approval}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="closed" data-testid="filter-closed">Closed</TabsTrigger>
                <TabsTrigger value="cancelled" data-testid="filter-cancelled">Cancelled</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Incident</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Service</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Opened</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">SLA</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Assignments</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400">Loading incidents…</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-slate-400" data-testid="empty-incidents">
                      {search ? 'No incidents match your search.' :
                       statusTab !== 'all' ? `No ${STATUS_LABEL[statusTab].toLowerCase()} incidents.` :
                       'No incidents yet. Log your first incident to get started.'}
                    </TableCell>
                  </TableRow>
                ) : filtered.map((inc, idx) => {
                  const openAssign = (inc.assignments || []).filter((a) => !a.ended_at).length;
                  const sla = computeSla(inc, serviceMap);
                  const tone = sla ? SLA_TONE_META[sla.tone] : null;
                  return (
                    <TableRow key={inc.id} className="hover:bg-slate-50/80 cursor-pointer" onClick={() => openView(inc)} data-testid={`incident-row-${idx}`}>
                      <TableCell className="font-mono text-sm text-[#0D5B69] font-semibold">{inc.incident_no}</TableCell>
                      <TableCell className="text-sm text-slate-700">{customerMap[inc.customer_id]?.company_name || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-700">{serviceMap[inc.primary_service_id]?.name || '—'}</TableCell>
                      <TableCell>
                        {inc.location_address ? (
                          <div className="flex items-start gap-1 text-xs text-slate-600 max-w-[200px]">
                            <MapPin className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                            <span className="truncate">{inc.location_address}</span>
                          </div>
                        ) : <span className="text-xs text-slate-400">No location</span>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{fmtTime(inc.opened_at)}</TableCell>
                      <TableCell data-testid={`sla-cell-${idx}`}>
                        {!sla || sla.tone === 'none' ? (
                          <span className="text-xs text-slate-400 italic">no SLA</span>
                        ) : (
                          <Badge
                            className={`${tone.bg} ${tone.text} border-current/15 text-[11px] font-semibold gap-1 hover:opacity-100 ${sla.tone === 'breach' ? 'animate-pulse' : ''}`}
                            data-sla-tone={sla.tone}
                            title={`${sla.slaMin}-min SLA · ${Math.floor(sla.pct)}% elapsed`}
                          >
                            <Timer className={`w-3 h-3 ${tone.text}`} />
                            {fmtSlaShort(sla)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(inc.assignments || []).length === 0 ? (
                          <span className="text-xs text-slate-400">None</span>
                        ) : (
                          <div className="flex gap-1 items-center">
                            <Badge variant="outline" className="text-[10px]">{inc.assignments.length} total</Badge>
                            {openAssign > 0 && <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] hover:bg-blue-100">{openAssign} active</Badge>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          <Badge className={`${STATUS_BADGE[inc.status]} hover:opacity-100`}>{STATUS_LABEL[inc.status]}</Badge>
                          {inc.duress_check_required && (
                            <Badge
                              className="bg-red-600 text-white border-red-700 text-[10px] font-bold gap-1 hover:bg-red-600 animate-pulse"
                              title="Cancelled from a partner app — a controller must confirm the caller is safe"
                              data-testid={`duress-badge-${idx}`}
                            >
                              <AlertTriangle className="w-3 h-3" /> DURESS?
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); openView(inc); }} data-testid={`view-incident-${idx}`}>
                          <Eye className="w-4 h-4 text-slate-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <IncidentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        incident={editing}
        customers={customers}
        contractors={contractors}
        services={services}
        defaultTab={dialogDefaultTab}
        onSaved={handleSaved}
      />
    </Layout>
  );
};

const StatCard = ({ label, value, icon: Icon, accent, pulse, testid }) => (
  <Card className="bg-white border-slate-200" data-testid={testid}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold font-heading" style={{ color: accent }}>{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${pulse ? 'animate-pulse' : ''}`} style={{ background: `${accent}15` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
      </div>
    </CardContent>
  </Card>
);
